// @flow
import { type SelectOptions, type SelectOption } from 'bench-internal-theme';
import type {
  Currency,
  FdsAccountWithConnection,
  FinancialInstitution,
  LedgerAccountDisplayGroup,
  Product,
} from '../types';
// $FlowFixMe - Cannot resolve module `../../utils/sortAlphabetical (it's a .tsx file)
import sortAlphabetical from '../../utils/sortAlphabetical';

type GroupedSelectOption = {
  label: string,
  options: Array<SelectOption>,
};

type DisplayGroupOption = SelectOption | GroupedSelectOption;

export const getCurrencyOptions = (availableCurrencies: Array<Currency>): SelectOptions =>
  availableCurrencies.map(currency => ({
    label: currency.name,
    value: currency.id,
  }));

const collectionMethods = [
  { value: 'PLAID', label: 'Plaid' },
  { value: 'THIRD_PARTY_LOGIN', label: '3rd Party Login' },
  { value: 'SHOPIFY', label: 'Shopify' },
  { value: 'GUSTO', label: 'Gusto' },
  { value: 'SQUARE', label: 'Square' },
  { value: 'STRIPE', label: 'Stripe' },
  { value: 'PAYPAL', label: 'Paypal' },
  { value: 'AMAZON', label: 'Amazon' },
  { value: 'FINCH', label: 'Finch' },
  { value: 'OTHER_INTEGRATION', label: 'Other Integration' },
  { value: 'NOT_REQUIRED', label: 'Not Required' },
  { value: 'CANNOT_OPTIMIZE', label: 'Cannot Optimize' },
  { value: 'REQUESTED', label: 'Requested' },
];

export const collectionMethodOptions: SelectOptions = collectionMethods;

const categories = [
  { id: '1', name: 'Assets' },
  { id: '3', name: 'Liabilities' },
  { id: '5', name: 'Equity' },
  { id: '6', name: 'Revenues' },
  { id: '7', name: 'Operating Expenses' },
  { id: '8', name: 'Cost of Sales' },
];

const categoriesWithPB = [...categories, { id: '9', name: 'Personal Account used for Business' }];

export const categoryOptions = (showPBCategory: boolean): SelectOptions =>
  (showPBCategory ? categoriesWithPB : categories).map(category => ({
    value: category.id,
    label: category.name,
  }));

const capitalizeFirstLetter = (string: string): string =>
  string
    .split('_')
    .map(i => i[0].toUpperCase() + i.substr(1).toLowerCase())
    .join(' ');

const formatPlaidFDSAccount = (fdsAccount: FdsAccountWithConnection, source: string): string => {
  const institutionName = fdsAccount.connection.financialInstitutionName;

  if (fdsAccount.number) {
    return `${institutionName} - ${fdsAccount.name} - ${fdsAccount.number} (${source})`;
  }

  return `${institutionName} - ${fdsAccount.name} (${source})`;
};

const formatIntegrationLabel = (fdsAccount: FdsAccountWithConnection): string => {
  const capitalizedSource = capitalizeFirstLetter(fdsAccount.connection.source);
  if (fdsAccount.connection.source === 'PLAID') {
    return formatPlaidFDSAccount(fdsAccount, capitalizedSource);
  }
  if (fdsAccount.connection.source === 'SHOPIFY') {
    return `${fdsAccount.connection.sourceId} (Shopify)`;
  }

  if (fdsAccount.connection.source === 'GUSTO') {
    return `${fdsAccount.name} (Gusto)`;
  }

  if (fdsAccount.connection.source === 'SQUARE') {
    return `${fdsAccount.name} (Square)`;
  }

  if (fdsAccount.name) {
    return `${fdsAccount.name} (${capitalizedSource})`;
  }

  return capitalizedSource;
};

export const integrationOptions = (fdsAccounts: Array<FdsAccountWithConnection>): SelectOptions =>
  fdsAccounts.map(fdsAccount => ({
    label: formatIntegrationLabel(fdsAccount),
    value: fdsAccount.id,
  }));

export const institutionOptions = (
  financialInstitutions: Array<FinancialInstitution>,
): SelectOptions =>
  financialInstitutions
    .map(financialInstitution => ({
      label: financialInstitution.isActive
        ? financialInstitution.name
        : `${financialInstitution.name} (Inactive)`,
      value: financialInstitution.id,
      metaData: { isActive: financialInstitution.isActive },
    }))
    .sort((a, b) => sortAlphabetical(a.label, b.label));

export const productTypeOptions = (products: Array<Product>): SelectOptions =>
  products
    .map(product => ({
      label: product.financialInstitutionProductType.name,
      value: product.id,
      metaData: {
        productTypeId: product.financialInstitutionProductType?.id,
      },
    }))
    .sort((a, b) => sortAlphabetical(a.label, b.label));

export const displayGroupOptions = (
  displayGroups: Array<LedgerAccountDisplayGroup>,
): Array<DisplayGroupOption> => {
  // Add "Hidden" option at the top
  const hiddenOption = {
    value: null,
    label: 'Hidden',
  };

  // Group display groups by parent category
  const grouped = displayGroups.reduce((acc, group) => {
    const parentName = group.parent.name;
    const parentId = group.parent.id;
    if (!acc[parentName]) {
      acc[parentName] = {
        label: parentName,
        options: [],
        parentId, // Store parentId for sorting
      };
    }
    acc[parentName].options.push({
      value: group.id,
      label: group.name,
    });
    return acc;
  }, {});

  const sortedGroups = (Object.values(grouped): any).sort((a, b) => {
    return parseInt(a.parentId, 10) - parseInt(b.parentId, 10);
  });

  // Return Hidden option first, followed by grouped options
  return [hiddenOption, ...sortedGroups];
};
